import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";

function generateDeviceId(): string {
  const stored = localStorage.getItem("nova_device_id");
  if (stored) return stored;
  const id = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem("nova_device_id", id);
  return id;
}

export interface DeviceInfo {
  id: string;
  deviceId: string;
  deviceName: string | null;
  deviceType: string | null;
  browser: string | null;
  os: string | null;
  lastActiveAt: string;
  isOnline: boolean;
  activeSessionId: string | null;
  activeView: string | null;
  childId: string | null;
}

export interface SyncState {
  deviceId: string;
  isConnected: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  otherDevices: DeviceInfo[];
  onlineCount: number;
  activeChildren: { childId: string; deviceName: string; view: string }[];
}

export interface SessionConflict {
  conflict: boolean;
  activeDevice?: DeviceInfo;
}

export function useDeviceSync(isAuthenticated: boolean) {
  const deviceId = useRef(generateDeviceId()).current;
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [syncEvents, setSyncEvents] = useState<any[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const queryClient = useQueryClient();

  const registerMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/sync/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ deviceId }),
      });
      if (!res.ok) throw new Error("Registration failed");
      return res.json();
    },
    onSuccess: () => {
      setIsConnected(true);
      setLastSyncAt(new Date());
    },
  });

  const { data: deviceSummary } = useQuery({
    queryKey: queryKeys.sync.devices,
    queryFn: async () => {
      const res = await fetch("/api/sync/devices", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: isAuthenticated && isConnected,
    refetchInterval: 30000,
    staleTime: 15000,
  });

  const sendHeartbeat = useCallback(async (payload: {
    childId?: string | null;
    activeView?: string;
    activeSessionId?: string | null;
  }) => {
    try {
      setIsSyncing(true);
      const res = await fetch("/api/sync/heartbeat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ deviceId, ...payload }),
      });
      if (res.ok) {
        setLastSyncAt(new Date());
        queryClient.invalidateQueries({ queryKey: queryKeys.sync.devices });
      }
    } catch {} finally {
      setIsSyncing(false);
    }
  }, [deviceId, queryClient]);

  const checkConflict = useCallback(async (childId: string): Promise<SessionConflict> => {
    try {
      const res = await fetch("/api/sync/check-conflict", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ childId, deviceId }),
      });
      if (!res.ok) return { conflict: false };
      return res.json();
    } catch {
      return { conflict: false };
    }
  }, [deviceId]);

  const requestHandoff = useCallback(async (childId: string, fromDeviceId: string, sessionId?: string) => {
    try {
      await fetch("/api/sync/handoff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ childId, fromDeviceId, toDeviceId: deviceId, sessionId }),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.sync.devices });
    } catch {}
  }, [deviceId, queryClient]);

  const emitEvent = useCallback(async (eventType: string, payload: any, childId?: string) => {
    try {
      await fetch("/api/sync/emit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ eventType, payload, childId, sourceDeviceId: deviceId }),
      });
    } catch {}
  }, [deviceId]);

  useEffect(() => {
    if (!isAuthenticated) return;

    registerMutation.mutate();

    const eventTypes = [
      "session_started", "session_ended", "session_progress",
      "child_selected", "view_changed", "device_connected",
      "device_disconnected", "achievement_earned", "mastery_updated",
      "session_handoff",
    ];

    const handleSyncEvent = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        setSyncEvents(prev => [...prev.slice(-49), { type: event.type, data, timestamp: Date.now() }]);

        if (["session_started", "session_ended", "session_progress", "achievement_earned", "mastery_updated"].includes(event.type)) {
          queryClient.invalidateQueries({ queryKey: queryKeys.sync.devices });
        }
      } catch {}
    };

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    function connectSSE() {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const es = new EventSource("/api/sync/stream", { withCredentials: true } as any);

      es.addEventListener("connected", () => {
        setIsConnected(true);
      });

      for (const type of eventTypes) {
        es.addEventListener(type, handleSyncEvent);
      }

      es.onerror = () => {
        setIsConnected(false);
        es.close();
        eventSourceRef.current = null;
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(connectSSE, 5000);
      };

      eventSourceRef.current = es;
    }

    connectSSE();

    heartbeatRef.current = setInterval(() => {
      sendHeartbeat({});
    }, 60000);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);

      fetch("/api/sync/disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ deviceId }),
        keepalive: true,
      }).catch(() => {});
    };
  }, [isAuthenticated]);

  const state: SyncState = {
    deviceId,
    isConnected,
    isSyncing,
    lastSyncAt,
    otherDevices: deviceSummary?.devices?.filter((d: any) => d.deviceId !== deviceId) || [],
    onlineCount: deviceSummary?.onlineCount || 0,
    activeChildren: deviceSummary?.activeChildren || [],
  };

  return {
    ...state,
    sendHeartbeat,
    checkConflict,
    requestHandoff,
    emitEvent,
    syncEvents,
  };
}
